import { Cubit } from "blac";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import { TranslationKey } from "src/types/translationKey";

export interface BlockingLoadingOverlayErrorProps {
  title?: string;
  message?: string;
  retry?: () => void;
  onCancel?: () => void;
  cancelText?: TranslationKey;
}

export interface BlockingLoadingOverlayState {
  status: "error" | "loading" | "none" | "success";
  error?: BlockingLoadingOverlayErrorProps;
  bg: "solid" | "transparent" | "branded" | "partner";
  fadeIn: boolean;
  title?: string | TranslationKey;
  message?: string | TranslationKey;
  cancelText?: TranslationKey;
}

export class BlockingLoadingOverlayBloc extends Cubit<BlockingLoadingOverlayState> {
  constructor() {
    super({
      status: "none",
      bg: "branded",
      fadeIn: true
    });
  }
  loadingCounter = 0;
  longLoadingReportTimeout: ReturnType<typeof setTimeout> | null = null;

  public readonly endLoading = () => {
    this.loadingCounter = Math.max(this.loadingCounter - 1, 0);
    if (this.loadingCounter === 0) {
      this.emit({
        ...this.state,
        status: "none",
        title: "",
        message: ""
      });

      if (this.longLoadingReportTimeout) {
        clearTimeout(this.longLoadingReportTimeout);
      }
    }
  };

  public readonly loadingSuccess = (
    options: {
      title?: string | TranslationKey;
      message?: string | TranslationKey;
    } = {}
  ) => {
    this.emit({
      ...this.state,
      status: "success",
      title: options.title ?? "",
      message: options.message ?? ""
    });

    let timeout = 1400;
    if (options.title) {
      timeout += 600;
    }
    if (options.message) {
      timeout += 600;
    }

    setTimeout(() => {
      this.endLoading();
    }, timeout);
  };

  public readonly loadingError = (error?: BlockingLoadingOverlayErrorProps) => {
    this.loadingCounter = Math.max(this.loadingCounter - 1, 0);
    this.emit({
      ...this.state,
      status: "error",
      error,
      title: error?.title ?? "",
      message: error?.message ?? "",
      cancelText: error?.cancelText ?? "cancel"
    });
    addSentryBreadcrumb(
      "loading.overlay",
      `Loading error: ${error?.title ?? "No title"}`
    );
    this.loadingCounter = Math.max(this.loadingCounter - 1, 0);
    if (this.loadingCounter === 0 && this.longLoadingReportTimeout) {
      clearTimeout(this.longLoadingReportTimeout);
    }
  };

  public readonly startLoading = (
    options: {
      bg?: BlockingLoadingOverlayState["bg"];
      fadeIn?: boolean;
      title?: string | TranslationKey;
      message?: string | TranslationKey;
    } = {}
  ) => {
    this.loadingCounter++;

    this.emit({
      ...this.state,
      error: undefined,
      status: "loading",
      bg: options.bg ?? "branded",
      fadeIn: options.fadeIn ?? true,
      title: options.title ?? "",
      message: options.message ?? ""
    });

    if (options.title) {
      addSentryBreadcrumb(
        "loading.overlay",
        `Loading started: ${options.title}`
      );
    }

    if (this.longLoadingReportTimeout) {
      clearTimeout(this.longLoadingReportTimeout);
    }

    this.longLoadingReportTimeout = setTimeout(() => {
      addSentryBreadcrumb(
        "loading.overlay",
        `Loading has been active for over 15 seconds`
      );
    }, 15000);

    return [this.loadingSuccess, this.loadingError];
  };

  public readonly callErrorRetry = () => {
    if (this.state.error?.retry) {
      this.state.error.retry();
    }
  };

  public readonly callOnCancel = () => {
    if (this.state.error?.onCancel) {
      this.state.error.onCancel();
    }
  };

  public readonly endAllLoadings = () => {
    addSentryBreadcrumb("loading.overlay", "Finishing all loading states");
    this.loadingCounter = 0;
    this.emit({
      ...this.state,
      status: "none",
      title: "",
      message: ""
    });
  };
}

const BlockingLoadingOverlayController = new BlockingLoadingOverlayBloc();
export default BlockingLoadingOverlayController;
